import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { patch, type Ingest, type StreamKey, type StreamKeyPatch } from '../api'
import { GraceSettings } from './GraceSettings'
import { graceSaveError, graceText } from './grace'
import { PrimaryButton, SecondaryButton } from './ui'
import { useSession } from '../session'

/**
 * The stream key's "Hold when OBS drops" in the OBS settings: a row with what is
 * in force that opens the setting; Save sends PATCH /api/stream-keys/{id}
 * {source_grace_ms} (null = the center's default). The key's destinations
 * without their own value follow it.
 */
export function KeyGrace({ streamKey, ingest, live }: { streamKey: StreamKey; ingest?: Ingest; live?: boolean }) {
  const qc = useQueryClient()
  const { user } = useSession()
  const saved = streamKey.source_grace_ms ?? null
  const def = ingest?.default_source_grace_ms
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<number | null>(saved)
  const [invalid, setInvalid] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(false)
  // a new form each time it opens: from what is saved now
  const [epoch, setEpoch] = useState(0)
  const revoked = !!streamKey.revoked_at
  const now = saved ?? def
  const dirty = value !== saved

  const toggle = () => {
    if (!open) {
      setValue(saved)
      setInvalid(false)
      setErr('')
      setEpoch((e) => e + 1)
    }
    setDone(false)
    setOpen((o) => !o)
  }

  const save = async () => {
    if (!dirty || invalid || busy) return
    setBusy(true)
    setErr('')
    try {
      const body: StreamKeyPatch = { source_grace_ms: value }
      await patch<StreamKey>(`/api/stream-keys/${streamKey.id}`, body)
      // destinations inherit it: their "as the stream key" changes too
      await Promise.all([qc.invalidateQueries({ queryKey: ['stream-keys'] }), qc.invalidateQueries({ queryKey: ['destinations'] })])
      setOpen(false)
      setDone(true)
    } catch (e) {
      setErr(graceSaveError(e, user))
      // a revoked key: the list learns it
      void qc.invalidateQueries({ queryKey: ['stream-keys'] })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="key-grace-block">
      <button type="button" data-testid="key-grace-toggle" aria-expanded={open} onClick={toggle}
        className="flex min-h-[44px] items-center justify-between gap-2 rounded-md border border-line-2 px-2.5 text-left text-[13px] text-text-soft hover:text-text md:min-h-9">
        <span>Hold when OBS drops</span>
        <span className="tabular font-mono text-xs text-muted" data-testid="key-grace-now">
          {now === undefined ? 'default' : graceText(now)}
          {saved === null ? ' · default' : ''} {open ? '▴' : '▾'}
        </span>
      </button>
      {done && !open && (
        <p role="status" data-testid="key-grace-saved" className="text-xs text-live">
          ✓ Saved. Counts from the next time OBS drops; destinations without their own value follow it.
        </p>
      )}
      {open && (
        <div className="flex flex-col gap-2.5">
          {revoked ? (
            <p className="text-xs text-fail" data-testid="key-grace-revoked">This key is revoked: its settings can’t be changed. Issue a new key first.</p>
          ) : (
            <GraceSettings key={epoch} testId="key-grace" value={value} saved={saved} inherited={def} inheritLabel="Default" inheritFrom="the center’s default"
              subject="the platforms" live={live} disabled={busy}
              onChange={(v, bad) => { setValue(v); setInvalid(bad); setErr('') }} />
          )}
          {err && <p role="alert" data-testid="key-grace-error" className="text-xs text-fail">{err}</p>}
          <div className="grid grid-cols-2 gap-2">
            <SecondaryButton className="max-md:min-h-[44px]" disabled={busy} onClick={toggle}>Cancel</SecondaryButton>
            <PrimaryButton className="max-md:min-h-[44px]" testId="key-grace-save" busy={busy} disabled={revoked || !dirty || invalid} onClick={() => void save()}>
              {busy ? 'Saving…' : err ? 'Try again' : 'Save'}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  )
}
